import React, { useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  ResponsiveContainer, Legend,
} from "recharts";

const DISTRICTS = ["All Districts", "Gandhinagar", "Tapi", "Anand", "Kutchh", "Rajkot"];

export default function ScenarioModeler({ predictions }) {
  const [intervention, setIntervention] = useState("");
  const [coverage, setCoverage] = useState(70);
  const [targetDistricts, setTargetDistricts] = useState(["All Districts"]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [interventions, setInterventions] = useState(null);

  // Load intervention list once
  React.useEffect(() => {
    fetch("/api/interventions")
      .then(r => r.json())
      .then(d => {
        setInterventions(d.interventions);
        if (d.interventions.length) setIntervention(d.interventions[0].key);
      })
      .catch(() => setError("Could not load interventions"));
  }, []);

  function toggleDistrict(d) {
    if (d === "All Districts") {
      setTargetDistricts(["All Districts"]);
      return;
    }
    setTargetDistricts(prev => {
      const filtered = prev.filter(x => x !== "All Districts");
      return filtered.includes(d) ? filtered.filter(x => x !== d) : [...filtered, d];
    });
  }

  async function runSimulation() {
    if (!intervention) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/scenario", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          predictions,
          intervention,
          coverage_pct: coverage,
          target_districts: targetDistricts,
        }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.detail || "Simulation failed");
      }
      setResult(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  const chartData = result
    ? [
        { metric: "Avg Risk", Before: +(result.before.avg_risk * 100).toFixed(1), After: +(result.after.avg_risk * 100).toFixed(1) },
        { metric: "High-Risk Count", Before: result.before.high_risk_count, After: result.after.high_risk_count },
      ]
    : [];

  const selectedInfo = interventions?.find(i => i.key === intervention);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* Control panel */}
      <div style={s.card}>
        <h3 style={s.title}>Intervention Scenario Modeler</h3>
        <p style={s.subtitle}>Simulate the projected impact of a public health intervention on the uploaded beneficiary cohort.</p>

        <div style={s.controls}>
          {/* Intervention select */}
          <div style={s.field}>
            <label style={s.label}>Intervention</label>
            {interventions ? (
              <select style={s.select} value={intervention} onChange={e => { setIntervention(e.target.value); setResult(null); }}>
                {interventions.map(i => (
                  <option key={i.key} value={i.key}>{i.icon} {i.label}</option>
                ))}
              </select>
            ) : <div style={s.loading}>Loading interventions…</div>}
            {selectedInfo && <div style={s.hint}>{selectedInfo.description}</div>}
          </div>

          {/* Coverage slider */}
          <div style={s.field}>
            <label style={s.label}>Coverage — {coverage}% of beneficiaries</label>
            <input
              type="range" min={10} max={100} step={5}
              value={coverage}
              onChange={e => { setCoverage(+e.target.value); setResult(null); }}
              style={s.slider}
            />
            <div style={s.sliderMarks}>
              <span>10%</span><span>50%</span><span>100%</span>
            </div>
          </div>

          {/* District chips */}
          <div style={s.field}>
            <label style={s.label}>Target Districts</label>
            <div style={s.chips}>
              {DISTRICTS.map(d => (
                <button
                  key={d}
                  style={{
                    ...s.chip,
                    ...(targetDistricts.includes(d) ? s.chipActive : {}),
                  }}
                  onClick={() => { toggleDistrict(d); setResult(null); }}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          <button style={s.runBtn} onClick={runSimulation} disabled={loading || !intervention}>
            {loading ? "⏳ Simulating…" : "▶ Run Simulation"}
          </button>
        </div>

        {error && <div style={s.error}>⚠️ {error}</div>}
      </div>

      {/* Results */}
      {result && (
        <>
          {/* Impact summary cards */}
          <div style={s.impactRow}>
            {[
              {
                label: "Risk Reduction",
                value: `${result.impact.risk_reduction_pct}%`,
                icon: "📉",
                color: "#057a55",
                bg: "#f0fdf4",
              },
              {
                label: "High-Risk Cases Avoided",
                value: result.impact.high_risk_reduced,
                icon: "🛡️",
                color: "#1a56db",
                bg: "#eff6ff",
              },
              {
                label: "Beneficiaries Helped",
                value: result.impact.beneficiaries_helped,
                icon: "👥",
                color: "#7c3aed",
                bg: "#faf5ff",
              },
              {
                label: "Coverage",
                value: `${result.coverage_pct}%`,
                icon: "🎯",
                color: "#d97706",
                bg: "#fffbeb",
              },
            ].map(c => (
              <div key={c.label} style={{ ...s.impactCard, background: c.bg, borderColor: c.color + "33" }}>
                <div style={{ fontSize: 26, marginBottom: 6 }}>{c.icon}</div>
                <div style={{ ...s.impactValue, color: c.color }}>{c.value}</div>
                <div style={s.impactLabel}>{c.label}</div>
              </div>
            ))}
          </div>

          {/* Chart */}
          <div style={s.card}>
            <h3 style={s.title}>
              {result.icon} {result.intervention} — Before vs After
            </h3>
            <p style={s.subtitle}>Districts: {result.target_districts.join(", ")}</p>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="metric" tick={{ fontSize: 13 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="Before" fill="#dc2626" radius={[6, 6, 0, 0]} />
                <Bar dataKey="After" fill="#057a55" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Before / After numeric summary */}
          <div style={s.beforeAfterRow}>
            <div style={{ ...s.baCard, borderColor: "#fca5a5" }}>
              <div style={s.baHead}>Before Intervention</div>
              <div style={s.baStat}>
                <span style={s.baNum}>{(result.before.avg_risk * 100).toFixed(1)}%</span>
                <span style={s.baLbl}>Average Risk</span>
              </div>
              <div style={s.baStat}>
                <span style={{ ...s.baNum, color: "#dc2626" }}>{result.before.high_risk_count}</span>
                <span style={s.baLbl}>High-Risk Beneficiaries</span>
              </div>
            </div>
            <div style={s.arrow}>→</div>
            <div style={{ ...s.baCard, borderColor: "#6ee7b7" }}>
              <div style={{ ...s.baHead, color: "#057a55" }}>After Intervention</div>
              <div style={s.baStat}>
                <span style={{ ...s.baNum, color: "#057a55" }}>{(result.after.avg_risk * 100).toFixed(1)}%</span>
                <span style={s.baLbl}>Average Risk</span>
              </div>
              <div style={s.baStat}>
                <span style={{ ...s.baNum, color: "#057a55" }}>{result.after.high_risk_count}</span>
                <span style={s.baLbl}>High-Risk Beneficiaries</span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const s = {
  card: {
    background: "#fff", borderRadius: 12, padding: 24,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  },
  title: { margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "#111827" },
  subtitle: { margin: "0 0 20px", fontSize: 13, color: "#6b7280" },
  controls: { display: "flex", flexDirection: "column", gap: 20 },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 13, fontWeight: 600, color: "#374151" },
  hint: { fontSize: 12, color: "#6b7280", fontStyle: "italic" },
  select: {
    border: "1px solid #d1d5db", borderRadius: 8, padding: "10px 12px",
    fontSize: 14, color: "#111827", background: "#fff", cursor: "pointer",
  },
  slider: { width: "100%", accentColor: "#1a56db" },
  sliderMarks: { display: "flex", justifyContent: "space-between", fontSize: 11, color: "#9ca3af" },
  chips: { display: "flex", gap: 8, flexWrap: "wrap" },
  chip: {
    padding: "6px 14px", border: "1px solid #d1d5db", borderRadius: 20,
    background: "#f9fafb", color: "#374151", fontSize: 13, cursor: "pointer",
    transition: "all 0.15s",
  },
  chipActive: {
    background: "#eff6ff", borderColor: "#1a56db", color: "#1a56db", fontWeight: 600,
  },
  runBtn: {
    padding: "13px 24px", fontSize: 15, fontWeight: 700, border: "none",
    borderRadius: 10, cursor: "pointer",
    background: "linear-gradient(135deg, #1a56db, #1e40af)",
    color: "#fff", boxShadow: "0 4px 14px rgba(26,86,219,0.35)",
    alignSelf: "flex-start",
  },
  error: {
    background: "#fef2f2", border: "1px solid #fca5a5",
    borderRadius: 8, padding: "10px 14px", color: "#991b1b", fontSize: 13, marginTop: 8,
  },
  loading: { color: "#9ca3af", fontSize: 13, padding: "10px 0" },

  impactRow: { display: "flex", gap: 16, flexWrap: "wrap" },
  impactCard: {
    flex: 1, minWidth: 130, border: "1px solid",
    borderRadius: 12, padding: "18px 16px", textAlign: "center",
    boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
  },
  impactValue: { fontSize: 28, fontWeight: 800, lineHeight: 1 },
  impactLabel: { fontSize: 12, color: "#6b7280", marginTop: 4 },

  beforeAfterRow: { display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" },
  baCard: {
    flex: 1, background: "#fff", border: "2px solid", borderRadius: 12,
    padding: 20, boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  },
  baHead: { fontWeight: 700, fontSize: 14, color: "#dc2626", marginBottom: 14 },
  baStat: { display: "flex", flexDirection: "column", marginBottom: 10 },
  baNum: { fontSize: 28, fontWeight: 800, color: "#111827" },
  baLbl: { fontSize: 12, color: "#6b7280" },
  arrow: { fontSize: 28, color: "#9ca3af", flexShrink: 0 },
};
